import React, { useState, useEffect } from 'react';
import {
  Settings,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  Plus,
  Trash2,
  Lock,
  Info,
  ChevronDown,
  Clock,
  XCircle,
  RefreshCw,
  History,
} from 'lucide-react';
import {
  useGradeConfig,
  useSaveGradeConfig,
  useStudentGrades,
  useSaveGrades,
  useFinalizeGrades,
} from '../../hooks/useProfessor';
import type {
  GradeComponent,
  StudentGradeResponse,
  StudentGradeEntry,
} from '@/types/professor.types';
import { GradeAbsenceStatus, StudentGradeStatusEnum } from '@/types/professor.types';
import { CuteConfirmModal } from '@/shared/components/CuteConfirmModal';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';

interface GradesTabProps {
  groupeId: string;
  moduleElementId: string;
  // Year being consulted; omitted means the live one.
  academicYearId?: string;
  // A closed year is consultation only — the API refuses writes outside the
  // current year, so the tab must not offer them.
  readOnly?: boolean;
}

// Generate a unique code for a component based on name
const generateComponentCode = (index: number, name: string): string => {
  const baseName = name
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^A-Z0-9]/g, '_')
    .replace(/_+/g, '_') // Replace multiple underscores with single
    .substring(0, 15);
  return `${baseName}_${index}`;
};

// Default grade components when starting fresh
const DEFAULT_GRADE_COMPONENTS: GradeComponent[] = [
  {
    code: 'PARTICIPATION',
    name: 'Participation/Assiduité',
    percentage: 20,
    maxScore: 20,
    isRequired: true,
    sortOrder: 0,
  },
  {
    code: 'CONTROLE_CONTINU',
    name: 'Contrôle Continu',
    percentage: 30,
    maxScore: 20,
    isRequired: true,
    sortOrder: 1,
  },
  {
    code: 'EXAMEN_PROJET',
    name: 'Examen/Projet Final',
    percentage: 50,
    maxScore: 20,
    isRequired: true,
    sortOrder: 2,
  },
];

// Type for grade edits - tracks score and absence status
interface GradeEditValue {
  score?: number | null;
  absenceStatus?: GradeAbsenceStatus;
}

// Track raw input values for decimal support (allows typing "15." or "15,")
type RawInputValues = Record<string, Record<string, string>>;

const GradesTab: React.FC<GradesTabProps> = ({
  groupeId,
  moduleElementId,
  academicYearId,
  readOnly = false,
}) => {
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [components, setComponents] = useState<GradeComponent[]>(DEFAULT_GRADE_COMPONENTS);
  const [gradeEdits, setGradeEdits] = useState<Record<string, Record<string, GradeEditValue>>>({});
  const [rawInputs, setRawInputs] = useState<RawInputValues>({}); // Track raw input for decimal support
  const [saveError, setSaveError] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null); // Track which dropdown is open
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const toast = useToast();

  // Fetch data
  const { data: config, isLoading: configLoading, refetch: refetchConfig } = useGradeConfig(groupeId, moduleElementId, academicYearId);
  const { data: studentGrades = [], isLoading: gradesLoading, refetch: refetchGrades } = useStudentGrades(
    config?.isValid ? groupeId : null,
    config?.isValid ? moduleElementId : null,
    academicYearId
  );

  // Mutations
  const saveConfigMutation = useSaveGradeConfig();
  const saveGradesMutation = useSaveGrades();
  const finalizeGradesMutation = useFinalizeGrades();

  // Initialize components when config loads (or use defaults if no config)
  useEffect(() => {
    if (config?.components && config.components.length > 0) {
      setComponents(config.components);
    } else if (!config || !config.isValid) {
      // Use default components for new configurations
      setComponents(DEFAULT_GRADE_COMPONENTS);
    }
  }, [config]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (openDropdown) {
        const target = e.target as HTMLElement;
        if (!target.closest('.relative')) {
          setOpenDropdown(null);
        }
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);

  // Calculate total percentage
  const totalPercentage = components.reduce((sum, c) => sum + (c.percentage || 0), 0);
  const isValidConfig = Math.abs(totalPercentage - 100) < 0.01;

  // Add new component with auto-generated code
  const handleAddComponent = () => {
    const newIndex = components.length;
    const newComponent: GradeComponent = {
      code: `COMP_${Date.now()}`, // Use timestamp for unique code
      name: 'Nouvelle composante',
      percentage: 0,
      maxScore: 20,
      isRequired: true,
      sortOrder: newIndex,
    };
    setComponents([...components, newComponent]);
  };

  // Remove component
  const handleRemoveComponent = (index: number) => {
    if (components.length <= 1) return;
    setComponents(components.filter((_, i) => i !== index));
  };

  // Update component - auto-regenerate code when name changes
  const handleUpdateComponent = (index: number, field: keyof GradeComponent, value: string | number | boolean) => {
    setComponents(
      components.map((c, i) => {
        if (i !== index) return c;

        const updated = { ...c, [field]: value };

        // Auto-generate code when name changes
        if (field === 'name' && typeof value === 'string') {
          updated.code = generateComponentCode(index, value);
        }

        return updated;
      })
    );
  };

  // Save configuration
  const handleSaveConfig = async () => {
    if (!isValidConfig) return;

    setSaveError(null);

    // Ensure all components have valid data for the backend
    const componentsToSave = components.map((c, index) => ({
      code: c.code || generateComponentCode(index, c.name),
      name: c.name,
      percentage: Number(c.percentage) || 0,
      maxScore: Number(c.maxScore) || 20,
      isRequired: c.isRequired ?? true,
      sortOrder: index,
    }));

    console.log('Saving grade config:', { groupeId, moduleElementId, components: componentsToSave });

    try {
      await saveConfigMutation.mutateAsync({
        groupeId,
        moduleElementId,
        dto: { components: componentsToSave },
      });
      setIsConfiguring(false);
      setSaveError(null);
    } catch (error: any) {
      console.error('Failed to save config:', error);
      setSaveError(error?.message || 'Échec de la sauvegarde. Veuillez réessayer.');
    }
  };

  // Handle grade change - supports comma and dot as decimal separator, and clearing
  const handleGradeChange = (studentId: string, componentCode: string, value: string) => {
    // Normalize: replace comma with dot for consistency
    const normalizedValue = value.replace(',', '.');

    // Allow clearing the grade (empty string)
    if (normalizedValue === '' || value === null || value === undefined) {
      setRawInputs((prev) => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          [componentCode]: '',
        },
      }));
      setGradeEdits((prev) => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          [componentCode]: { score: null, absenceStatus: GradeAbsenceStatus.NONE },
        },
      }));
      return;
    }

    // Validate input: allow digits, one dot, and partial decimals like "15." or ".5"
    const validPattern = /^-?\d*\.?\d*$/;
    if (!validPattern.test(normalizedValue)) return;

    // Store raw input to allow typing decimals (e.g., "15." before completing "15.25")
    setRawInputs((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [componentCode]: normalizedValue,
      },
    }));

    // Parse the number (will work for complete numbers, partial decimals parse as the integer part)
    const numValue = parseFloat(normalizedValue);

    // Update grade edits only if we have a valid number
    if (!isNaN(numValue)) {
      setGradeEdits((prev) => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          [componentCode]: {
            ...prev[studentId]?.[componentCode],
            score: numValue,
            absenceStatus: GradeAbsenceStatus.NONE,
          },
        },
      }));
    }
  };

  // Handle absence status change
  const handleAbsenceChange = (studentId: string, componentCode: string, absenceStatus: GradeAbsenceStatus) => {
    // Clear raw input when changing absence status
    setRawInputs((prev) => {
      const updated = { ...prev };
      if (updated[studentId]) {
        const { [componentCode]: _, ...rest } = updated[studentId];
        updated[studentId] = rest;
      }
      return updated;
    });
    setGradeEdits((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [componentCode]: {
          score: absenceStatus !== GradeAbsenceStatus.NONE ? 0 : prev[studentId]?.[componentCode]?.score ?? null,
          absenceStatus,
        },
      },
    }));
    setOpenDropdown(null);
  };

  // Calculate reactive average for a student (including unsaved edits)
  const calculateReactiveAverage = (student: StudentGradeResponse): number | null => {
    if (!config?.components) return student.averageGrade;

    let totalWeightedScore = 0;
    let totalPercentage = 0;
    let hasAnyGrade = false;

    for (const comp of config.components) {
      const existingGrade = student.componentGrades.find((g) => g.componentCode === comp.code);
      const editedValue = gradeEdits[student.studentId]?.[comp.code];

      // Get the current score (edited value takes precedence)
      let score: number | null = null;
      if (editedValue?.score !== undefined) {
        score = editedValue.score;
      } else if (existingGrade?.score !== undefined && existingGrade?.score !== null) {
        score = existingGrade.score;
      }

      if (score !== null && score !== undefined) {
        hasAnyGrade = true;
        // Normalize score to 20 scale if needed, then apply percentage weight
        const normalizedScore = (score / (comp.maxScore || 20)) * 20;
        totalWeightedScore += normalizedScore * (comp.percentage / 100);
        totalPercentage += comp.percentage;
      }
    }

    if (!hasAnyGrade || totalPercentage === 0) return null;

    // Return weighted average (already on 20 scale)
    return totalWeightedScore;
  };

  // Render status badge based on student status
  const renderStatusBadge = (status: StudentGradeStatusEnum, statusReason?: string) => {
    switch (status) {
      case StudentGradeStatusEnum.VALIDATED:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="h-3 w-3" />
            Validé
          </span>
        );
      case StudentGradeStatusEnum.RATTRAPAGE:
        return (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium cursor-help"
            title={statusReason || 'Rattrapage requis'}
          >
            <RefreshCw className="h-3 w-3" />
            Rattrapage
          </span>
        );
      case StudentGradeStatusEnum.FAILED:
        return (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium cursor-help"
            title={statusReason || 'Échec'}
          >
            <XCircle className="h-3 w-3" />
            Échec
          </span>
        );
      case StudentGradeStatusEnum.INCOMPLETE:
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
            <AlertCircle className="h-3 w-3" />
            Incomplet
          </span>
        );
    }
  };

  // Save grades
  const handleSaveGrades = async () => {
    const grades: StudentGradeEntry[] = [];

    Object.entries(gradeEdits).forEach(([studentId, componentGrades]) => {
      Object.entries(componentGrades).forEach(([componentCode, editValue]) => {
        grades.push({
          studentId,
          componentCode,
          score: editValue.score,
          absenceStatus: editValue.absenceStatus || GradeAbsenceStatus.NONE,
        });
      });
    });

    if (grades.length === 0) return;

    try {
      await saveGradesMutation.mutateAsync({
        groupeId,
        moduleElementId,
        dto: { grades },
      });
      setGradeEdits({});
      setRawInputs({}); // Clear raw inputs after successful save
      refetchGrades();
    } catch (error) {
      console.error('Failed to save grades:', error);
    }
  };

  // Finalizing CLOSES the sheet: anything still blank is recorded as 0 rather
  // than blocking the professor. The count is named in the confirmation so a
  // forgotten student is never zeroed silently.
  const incompleteCount = studentGrades.filter(
    (s: StudentGradeResponse) => !s.isComplete
  ).length;

  const handleFinalizeClick = () => {
    if (Object.keys(gradeEdits).length > 0) {
      toast.error(
        'Modifications non enregistrées',
        'Enregistrez vos saisies avant de finaliser les notes.'
      );
      return;
    }
    setShowFinalizeModal(true);
  };

  // Finalize grades - Step 2: Confirm and execute
  const handleConfirmFinalize = async () => {
    setShowFinalizeModal(false);

    try {
      const result = await finalizeGradesMutation.mutateAsync({ groupeId, moduleElementId });

      // Refetch both config (to get isFinalized status) and grades
      await Promise.all([refetchConfig(), refetchGrades()]);

      // Show success message with rattrapage info if applicable
      const details = [
        result.zerosWritten ? `${result.zerosWritten} note(s) vide(s) enregistrée(s) à 0.` : null,
        result.rattrapagesCreated
          ? `${result.rattrapagesCreated} rattrapage(s) créés pour les étudiants ayant échoué.`
          : null,
      ]
        .filter(Boolean)
        .join(' ');
      toast.success('Notes finalisées avec succès', details || undefined);
    } catch (error: any) {
      console.error('Failed to finalize grades:', error);
      toast.error(
        'Échec de la finalisation',
        error?.message || 'Veuillez réessayer.'
      );
    }
  };

  if (configLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Render configuration form (used in both initial setup and edit mode)
  const renderConfigForm = () => (
    <div className="p-4">
      <div className="space-y-3">
        {components.map((component, index) => (
          <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="text"
              value={component.name}
              onChange={(e) => handleUpdateComponent(index, 'name', e.target.value)}
              placeholder="Nom de la composante"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={component.percentage}
                onChange={(e) => handleUpdateComponent(index, 'percentage', parseFloat(e.target.value) || 0)}
                min="0"
                max="100"
                className="w-20 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-500">/</span>
              <input
                type="number"
                value={component.maxScore}
                onChange={(e) => handleUpdateComponent(index, 'maxScore', parseFloat(e.target.value) || 20)}
                min="1"
                className="w-16 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              onClick={() => handleRemoveComponent(index)}
              disabled={components.length <= 1}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title={components.length <= 1 ? 'Au moins une composante requise' : 'Supprimer'}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={handleAddComponent}
        className="mt-4 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
      >
        <Plus className="h-4 w-4" />
        Ajouter une composante
      </button>
    </div>
  );

  // No configuration yet
  if (!config || !config.isValid) {
    return (
      <div className="space-y-6">
        {/* Configuration needed message */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <Info className="h-6 w-6 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-800">Configuration des notes</h3>
              <p className="text-sm text-blue-700 mt-1">
                Une configuration par défaut est proposée (Participation 20%, Contrôle Continu 30%, Examen/Projet Final 50%).
                Vous pouvez la modifier selon vos besoins.
              </p>
            </div>
          </div>
        </div>

        {/* Configuration panel */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Configuration des notes</h3>
          </div>

          {renderConfigForm()}

          {saveError && (
            <div className="px-4 py-2 bg-red-50 border-t border-red-200">
              <p className="text-sm text-red-600">{saveError}</p>
            </div>
          )}

          <div className="p-4 border-t border-gray-200 flex items-center justify-between">
            <div className={`text-sm font-medium ${isValidConfig ? 'text-green-600' : 'text-red-600'}`}>
              Total: {totalPercentage.toFixed(0)}% {isValidConfig ? '✓' : '(doit être 100%)'}
            </div>
            <button
              onClick={handleSaveConfig}
              disabled={!isValidConfig || saveConfigMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saveConfigMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Enregistrer la configuration
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Grade entry interface
  return (
    <div className="space-y-6">
      {/* Config summary or editing panel */}
      {isConfiguring ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Modifier la configuration des notes</h3>
          </div>

          {renderConfigForm()}

          {saveError && (
            <div className="px-4 py-2 bg-red-50 border-t border-red-200">
              <p className="text-sm text-red-600">{saveError}</p>
            </div>
          )}

          <div className="p-4 border-t border-gray-200 flex items-center justify-between">
            <div className={`text-sm font-medium ${isValidConfig ? 'text-green-600' : 'text-red-600'}`}>
              Total: {totalPercentage.toFixed(0)}% {isValidConfig ? '✓' : '(doit être 100%)'}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setIsConfiguring(false);
                  setSaveError(null);
                  // Reset components to current config
                  if (config?.components && config.components.length > 0) {
                    setComponents(config.components);
                  }
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveConfig}
                disabled={!isValidConfig || saveConfigMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saveConfigMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-wrap">
              <h3 className="font-semibold text-gray-900">Composantes de notation</h3>
              <div className="flex gap-2 flex-wrap">
                {config.components.map((c) => (
                  <span key={c.code} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                    {c.name}: {c.percentage}%
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={() => setIsConfiguring(true)}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              <Settings className="h-4 w-4" />
              Modifier
            </button>
          </div>
        </div>
      )}

      {/* Grade entry table - hidden when editing config */}
      {!isConfiguring && config && (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        {/* Finalization status banner */}
        {(config?.isFinalized || readOnly) && (
          <div className="bg-green-50 border-b border-green-200 p-4 flex items-center gap-3">
            <div className="flex-shrink-0">
              <div className="p-2 bg-green-100 rounded-full">
                <Lock className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-green-800 text-sm">Notes finalisées</h4>
              <p className="text-sm text-green-700 mt-0.5">
                Les notes ont été finalisées et ne peuvent plus être modifiées.
              </p>
            </div>
          </div>
        )}

        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Saisie des notes</h3>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Info className="h-4 w-4" />
            Les notes sont sur {config.components[0]?.maxScore || 20}
          </div>
        </div>

        {gradesLoading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-b-xl">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50">
                    Étudiant
                  </th>
                  {config.components.map((c) => (
                    <th key={c.code} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      <div>{c.name}</div>
                      <div className="font-normal text-gray-400">({c.percentage}%)</div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Moyenne
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Statut
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {studentGrades.map((student: StudentGradeResponse) => (
                  <tr
                    key={student.studentId}
                    className={student.isFormerMember ? 'bg-amber-50/40 hover:bg-amber-50' : 'hover:bg-gray-50'}
                  >
                    <td
                      className={`px-6 py-3 whitespace-nowrap sticky left-0 ${
                        student.isFormerMember ? 'bg-amber-50/40' : 'bg-white'
                      }`}
                    >
                      <div>
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                          <span>
                            {student.lastName} {student.firstName}
                          </span>
                          {student.isFormerMember && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[11px] font-medium rounded-full"
                              title="Cet étudiant a quitté le groupe ; ses notes saisies ici restent modifiables pour clôturer la feuille"
                            >
                              <History className="h-3 w-3" />
                              Ancien membre
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{student.studentCode}</div>
                      </div>
                    </td>
                    {config.components.map((comp) => {
                      const grade = student.componentGrades.find((g) => g.componentCode === comp.code);
                      const editedValue = gradeEdits[student.studentId]?.[comp.code];
                      const rawInput = rawInputs[student.studentId]?.[comp.code];
                      // Use raw input if available (for decimal typing), otherwise use parsed score or original grade
                      const displayValue = rawInput !== undefined
                        ? rawInput
                        : (editedValue?.score !== undefined
                            ? (editedValue.score !== null ? editedValue.score.toString() : '')
                            : (grade?.score !== undefined && grade?.score !== null ? grade.score.toString() : ''));
                      const currentAbsence = editedValue?.absenceStatus !== undefined
                        ? editedValue.absenceStatus
                        : (grade?.absenceStatus || GradeAbsenceStatus.NONE);
                      const dropdownKey = `${student.studentId}-${comp.code}`;
                      const isDropdownOpen = openDropdown === dropdownKey;
                      const hasEdit = editedValue !== undefined;

                      return (
                        <td key={comp.code} className="px-2 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {/* Grade input or Absence badge */}
                            {currentAbsence === GradeAbsenceStatus.NONE ? (
                              <input
                                type="text"
                                inputMode="decimal"
                                value={displayValue}
                                onChange={(e) => handleGradeChange(student.studentId, comp.code, e.target.value)}
                                disabled={config?.isFinalized || readOnly}
                                className={`w-16 px-2 py-1 border rounded text-center text-sm font-medium ${
                                  hasEdit
                                    ? 'border-blue-400 bg-blue-50'
                                    : grade?.isFromRattrapage
                                      ? 'border-orange-300 bg-orange-50 text-orange-700'
                                      : 'border-gray-300'
                                } ${config?.isFinalized || readOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                                placeholder="--"
                                title={grade?.isFromRattrapage && grade?.originalScore !== null
                                  ? `Note de rattrapage (Original: ${grade.originalScore.toFixed(2)}/${comp.maxScore})`
                                  : undefined
                                }
                              />
                            ) : (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                currentAbsence === GradeAbsenceStatus.ABSENT_EXCUSED
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {currentAbsence === GradeAbsenceStatus.ABSENT_EXCUSED ? 'AE' : 'ANE'}
                              </span>
                            )}

                            {/* Dropdown for absence status */}
                            {!config?.isFinalized && !readOnly && (
                            <div className="relative">
                              <button
                                onClick={() => setOpenDropdown(isDropdownOpen ? null : dropdownKey)}
                                className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                                title="Marquer absence"
                              >
                                <ChevronDown className="h-3 w-3" />
                              </button>

                              {isDropdownOpen && (
                                <div className="absolute z-[9999] right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                                  <button
                                    onClick={() => handleAbsenceChange(student.studentId, comp.code, GradeAbsenceStatus.NONE)}
                                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 ${
                                      currentAbsence === GradeAbsenceStatus.NONE ? 'bg-gray-50' : ''
                                    }`}
                                  >
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                    Note normale
                                  </button>
                                  <button
                                    onClick={() => handleAbsenceChange(student.studentId, comp.code, GradeAbsenceStatus.ABSENT_EXCUSED)}
                                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 ${
                                      currentAbsence === GradeAbsenceStatus.ABSENT_EXCUSED ? 'bg-amber-50' : ''
                                    }`}
                                  >
                                    <Clock className="h-4 w-4 text-amber-500" />
                                    Absent excusé
                                  </button>
                                  <button
                                    onClick={() => handleAbsenceChange(student.studentId, comp.code, GradeAbsenceStatus.ABSENT_UNEXCUSED)}
                                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 ${
                                      currentAbsence === GradeAbsenceStatus.ABSENT_UNEXCUSED ? 'bg-red-50' : ''
                                    }`}
                                  >
                                    <XCircle className="h-4 w-4 text-red-500" />
                                    Absent non-excusé
                                  </button>
                                </div>
                              )}
                            </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-center">
                      {(() => {
                        const reactiveAverage = calculateReactiveAverage(student);
                        const hasUnsavedChanges = gradeEdits[student.studentId] !== undefined;
                        return (
                          <span className={`font-medium ${
                            reactiveAverage !== null
                              ? reactiveAverage >= 10
                                ? 'text-green-600'
                                : 'text-red-600'
                              : 'text-gray-400'
                          } ${hasUnsavedChanges ? 'italic' : ''}`}>
                            {reactiveAverage !== null ? reactiveAverage.toFixed(2) : '--'}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {renderStatusBadge(student.status, student.statusReason)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!config?.isFinalized && !readOnly && (
        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {Object.keys(gradeEdits).length > 0 && (
              <span className="text-blue-600">
                {Object.values(gradeEdits).reduce((sum, g) => sum + Object.keys(g).length, 0)} modifications non enregistrées
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSaveGrades}
              disabled={Object.keys(gradeEdits).length === 0 || saveGradesMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saveGradesMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Enregistrer
            </button>
            <button
              onClick={handleFinalizeClick}
              disabled={finalizeGradesMutation.isPending || !studentGrades.every((s: StudentGradeResponse) => s.isComplete)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {finalizeGradesMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Lock className="h-4 w-4" />
              )}
              Finaliser
            </button>
          </div>
        </div>
        )}
      </div>
      )}

      {/* Finalize Confirmation Modal */}
      <CuteConfirmModal
        isOpen={showFinalizeModal}
        title="Finaliser les notes ?"
        message={
          incompleteCount > 0
            ? `${incompleteCount} étudiant(s) n'ont pas toutes leurs notes : les composantes vides seront enregistrées à 0/20.\n\nCette action est irréversible. Une fois finalisées, les notes ne pourront plus être modifiées. Les étudiants ayant échoué seront automatiquement inscrits en rattrapage.`
            : "Cette action est irréversible. Une fois finalisées, les notes ne pourront plus être modifiées. Les étudiants ayant échoué seront automatiquement inscrits en rattrapage."
        }
        confirmLabel="Oui, finaliser"
        cancelLabel="Annuler"
        variant="warning"
        onConfirm={handleConfirmFinalize}
        onCancel={() => setShowFinalizeModal(false)}
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </div>
  );
};

export default GradesTab;
